// Header do orçamento para fornecedores

import React from "react";
import { HiLocationMarker, HiMail, HiPhone, HiUser } from "react-icons/hi";
import Logo from "../../assets/images/monumenta.png";
import { formatPeriodoEvento } from "../../utils/orcamento-formatters";
import { ProdutorContato } from "../../types/orcamento-fornecedor.types";

interface OrcamentoHeaderProps {
  codigoOrcamento: string;
  nomeEvento: string;
  nomeCliente: string;
  dataInicio?: string;
  dataFim?: string | null;
  localEvento?: string | null;
  ufEvento?: string | null;
  produtores?: ProdutorContato[];
}

export const OrcamentoHeader: React.FC<OrcamentoHeaderProps> = ({
  codigoOrcamento,
  nomeEvento,
  nomeCliente,
  dataInicio,
  dataFim,
  localEvento,
  ufEvento,
  produtores,
}) => {
  const dateRange = formatPeriodoEvento(dataInicio, dataFim);

  // "Av. Paulista, 1000 - SP". A UF só entra quando existe.
  const localCompleto = localEvento
    ? [localEvento.trim(), ufEvento?.trim().toUpperCase()]
        .filter(Boolean)
        .join(" - ")
    : null;

  const produtoresValidos = (produtores ?? []).filter((p) => p.email);
  return (
    <header className="bg-[#161519] shadow-lg border-b-2 border-[#d2fa52]">
      <div className="container mx-auto px-4 py-6 max-w-6xl">
        {/* Logo e Título */}
        <div className="flex items-center gap-4 mb-6">
          <div className="bg-white rounded-lg p-2 w-14 h-14 flex items-center justify-center">
            <img
              src={Logo}
              alt="Monumenta"
              className="w-full h-full object-contain"
            />
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-white">
              Sistema de Cotação
            </h1>
            <p className="text-sm text-[#d2fa52]">Preenchimento de Orçamento</p>
          </div>
        </div>

        {/* Informações do Orçamento */}
        <div className="flex flex-wrap gap-3">
          <div className="bg-white/5 backdrop-blur-sm p-3 rounded-lg border border-white/10 shrink-0">
            <span className="text-xs text-gray-400 uppercase tracking-wider block mb-1">
              Código
            </span>
            <span className="text-base font-mono font-semibold text-white">
              {codigoOrcamento}
            </span>
          </div>
          <div className="bg-white/5 backdrop-blur-sm p-3 rounded-lg border border-white/10 flex-1 min-w-[200px]">
            <span className="text-xs text-gray-400 uppercase tracking-wider block mb-1">
              Projeto/Evento
            </span>
            <span className="text-base font-semibold text-white truncate block">
              {nomeEvento}
            </span>
          </div>
          <div className="bg-white/5 backdrop-blur-sm p-3 rounded-lg border border-white/10 shrink-0">
            <span className="text-xs text-gray-400 uppercase tracking-wider block mb-1">
              Cliente
            </span>
            <span className="text-base font-semibold text-white truncate block">
              {nomeCliente}
            </span>
          </div>
          {dateRange && (
            <div className="bg-white/5 backdrop-blur-sm p-3 rounded-lg border border-white/10 shrink-0">
              <span className="text-xs text-gray-400 uppercase tracking-wider block mb-1">
                Data
              </span>
              <span className="text-base font-semibold text-white whitespace-nowrap">
                {dateRange}
              </span>
            </div>
          )}
        </div>

        {/* Local do evento e contato do produtor: o fornecedor precisa saber
            onde o evento acontece e com quem falar, então ficam em destaque
            fora da faixa de metadados. */}
        {(localCompleto || produtoresValidos.length > 0) && (
          <div className="grid gap-3 mt-3 md:grid-cols-2">
            {localCompleto && (
              <div className="bg-[#d2fa52]/10 border-l-4 border-[#d2fa52] rounded-lg p-4">
                <span className="flex items-center gap-2 text-xs text-[#d2fa52] uppercase tracking-wider font-semibold mb-1">
                  <HiLocationMarker className="h-4 w-4 shrink-0" />
                  Local do evento
                </span>
                <span className="text-lg font-bold text-white block wrap-break-word">
                  {localCompleto}
                </span>
              </div>
            )}

            {produtoresValidos.length > 0 && (
              <div className="bg-[#d2fa52]/10 border-l-4 border-[#d2fa52] rounded-lg p-4">
                <span className="flex items-center gap-2 text-xs text-[#d2fa52] uppercase tracking-wider font-semibold mb-1">
                  <HiUser className="h-4 w-4 shrink-0" />
                  {produtoresValidos.length > 1
                    ? "Responsáveis pelo pedido"
                    : "Responsável pelo pedido"}
                </span>
                <div className="space-y-2">
                  {produtoresValidos.map((produtor) => (
                    <div key={produtor.email}>
                      {produtor.nome && (
                        <span className="text-lg font-bold text-white block wrap-break-word">
                          {produtor.nome}
                        </span>
                      )}
                      <a
                        href={`mailto:${produtor.email}`}
                        className="flex items-center gap-2 text-sm text-white hover:text-[#d2fa52] transition-colors break-all"
                      >
                        <HiMail className="h-4 w-4 shrink-0" />
                        {produtor.email}
                      </a>
                      {produtor.telefone && (
                        <a
                          href={`tel:${produtor.telefone.replace(/\D/g, "")}`}
                          className="flex items-center gap-2 text-sm text-white hover:text-[#d2fa52] transition-colors"
                        >
                          <HiPhone className="h-4 w-4 shrink-0" />
                          {produtor.telefone}
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
};
